export default function WhichPathSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Which Path Is Right for Me?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Not sure whether to buy online or use a broker? Here's how to decide.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-8 rounded-2xl border-2 border-teal-200 shadow-lg">
            <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6" aria-hidden="true">
              <i className="ri-shopping-cart-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Buy Online If You...</h3>
            <ul className="space-y-4 list-none p-0" aria-label="Reasons to buy online">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-teal-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Know exactly what you want</strong> — make, model, year, features</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-teal-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Want to shop at your own pace</strong> — browse, compare, decide when ready</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-teal-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>See something you like in our inventory</strong> — ready to buy now</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-teal-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Prefer a faster timeline</strong> — 3-7 days from browsing to delivery</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-teal-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Comfortable making decisions independently</strong> — no hand-holding needed</span>
              </li>
            </ul>
            <a 
              href="/inventory" 
              className="mt-8 w-full bg-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
              Browse Inventory
            </a>
          </div>

          <div className="bg-white p-8 rounded-2xl border-2 border-orange-200 shadow-lg">
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6" aria-hidden="true">
              <i className="ri-user-star-line text-3xl text-orange-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Use a Broker If You...</h3>
            <ul className="space-y-4 list-none p-0" aria-label="Reasons to use a broker">
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-orange-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Need help finding the right vehicle</strong> — not sure what fits your needs</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-orange-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Want access to any vehicle nationwide</strong> — not just what's in stock</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-orange-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Hate negotiating with dealers</strong> — let an expert handle it for you</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-orange-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Want someone on your side</strong> — advocacy, not sales pressure</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-line text-xl text-orange-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                <span className="text-[#374151]"><strong>Value expert guidance</strong> — inspection, history check, negotiation included</span>
              </li>
            </ul>
            <a 
              href="/broker-services" 
              className="mt-8 w-full bg-orange-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-700 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
              Get a Broker
            </a>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-teal-600 to-orange-600 p-8 rounded-2xl text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Still Not Sure?</h3>
          <p className="text-lg mb-6 max-w-2xl mx-auto">
            Start by browsing our inventory. If you don't see what you want, switch to broker mode — we'll find it for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/inventory" 
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
            >
              Browse First
            </a>
            <a 
              href="/contact" 
              className="bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition whitespace-nowrap inline-flex items-center justify-center gap-2 cursor-pointer"
            >
              Ask Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}